import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { authApi } from "../../api/auth";
import { ApiError, saveToken } from "../../api/client";
import { businessesApi } from "../../api/businesses";
import type { AuthResponse } from "../../types/models";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card } from "../../components/ui/Card";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  tenantId: z.string().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  // Sent here by the store when the server ended the login (#159).
  const sessionEnded = new URLSearchParams(location.search).get("reason") === "session_ended";


  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Set once the password is right and two-step is on: the second step (#159).
  // Verifying an email or a phone can end at the second step too: those pages
  // hand the token over in the navigation state.
  const [mfaToken, setMfaToken] = useState<string | null>(
    (location.state as { mfaToken?: string } | null)?.mfaToken ?? null,
  );
  const [tenantForSecondStep, setTenantForSecondStep] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setLoading(true);

    try {
      const response = await authApi.signIn(
        data.username.trim(),
        data.password,
        (data.tenantId || '').trim()
      );
      
      if (response.mfa_required && response.mfa_token) {
        setMfaToken(response.mfa_token);
        setTenantForSecondStep(data.tenantId || "");
        return;
      }

      await completeSignIn(response, data.tenantId, data.password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Login failed";
      if (msg === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(data.username.trim())}`);
        return;
      }
      if (msg === "phone_not_verified") {
        const phone = err instanceof ApiError ? (err.data as { phone?: string })?.phone ?? "" : "";
        const params = new URLSearchParams({ phone });
        navigate(`/verify-phone?${params.toString()}`);
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Everything after a sign-in is known good: the forced password change, or
  // the session and the business to open.
  async function completeSignIn(response: AuthResponse, tenantId: string | undefined, password?: string) {
    if (response.require_password_change && response.temp_token) {
      saveToken(response.temp_token);
      navigate("/change-password", { state: { currentPassword: password } });
      return;
    }

    setAuth(response.token, response.user, response.organization, response.businesses, response.current_business_id, response.current_outlet_id);

    let businesses = response.businesses;
    if (!businesses) {
      try {
        businesses = await businessesApi.listBusinesses();
        useAuthStore.getState().setAllBusinesses(businesses);
      } catch (e) {
        console.warn("Failed to fetch businesses after login:", e);
      }
    }

    // Resolve business and outlet selections based on tenantId and fallback auto-selects.
    await useAuthStore.getState().resolveBusinessAndOutlet(tenantId);

    const from = (location.state as { from?: string })?.from || "/dashboard";
    navigate(from, { replace: true });
  }

  const onSecondStep = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.verifySecondStep(mfaToken, useRecovery ? { recoveryCode: code } : { code });
      await completeSignIn(response, tenantForSecondStep);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const startAgain = () => {
    setMfaToken(null);
    setCode("");
    setUseRecovery(false);
    setError(null);
  };

  if (mfaToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
        <Card className="w-full max-w-md" title={t("auth.twoStepTitle")}>
          <form onSubmit={onSecondStep} className="space-y-4">
            <p className="text-sm text-text-muted">
              {useRecovery ? t("auth.twoStepRecoveryPrompt") : t("auth.twoStepCodePrompt")}
            </p>
            {error && (
              <div role="alert" className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}
            <Input
              label={useRecovery ? t("auth.twoStepRecoveryCode") : t("auth.twoStepCode")}
              type="text"
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button type="submit" className="w-full" loading={loading}>
              {t("auth.twoStepVerify")}
            </Button>
            <div className="flex justify-between text-sm">
              <button
                type="button"
                className="text-brand hover:underline"
                onClick={() => {
                  setUseRecovery(!useRecovery);
                  setCode("");
                  setError(null);
                }}
              >
                {useRecovery ? t("auth.twoStepUseApp") : t("auth.twoStepUseRecovery")}
              </button>
              <button type="button" className="text-text-muted hover:underline" onClick={startAgain}>
                {t("auth.twoStepStartAgain")}
              </button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
      <Card className="w-full max-w-md" title={t("auth.welcomeBack")}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {sessionEnded && !error && (
            <div role="status" className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-md text-sm">
              {t("auth.sessionEnded")}
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          <Input
            label={t("auth.username")}
            type="text"
            placeholder={t("auth.enterYourUsername")}
            error={errors.username?.message}
            {...register("username")}
          />

          <Input
            label={t("auth.password")}
            type="password"
            placeholder="••••••••"
            error={errors.password?.message}
            {...register("password")}
          />

          <Input
            label={t("auth.tenantIdOptional")}
            type="text"
            placeholder={t("auth.leaveEmptyForDefault")}
            error={errors.tenantId?.message}
            {...register("tenantId")}
          />

          <div className="flex items-center justify-between">
            <Link
              to="/forgot-password"
              className="text-sm text-brand hover:underline"
            >
              {t("auth.forgotPassword")}
            </Link>
          </div>

          <Button type="submit" className="w-full" loading={loading}>
            {t("auth.signIn")}
          </Button>

          <p className="text-center text-sm text-text-muted">
            {t("auth.newToProduct")}{" "}
            <Link to="/signup" className="text-brand hover:underline">
              {t("auth.createAnAccount")}
            </Link>
          </p>

        </form>
      </Card>
    </div>
  );
}
